const CART_LINES_UPDATE_EVENT = 'shopify:cart:lines-update';
const RAPI_TOGGLE_SELECTOR =
  'input[data-rapi-cs-toggle]:checked, input[class*="add_cross_sell_"]:checked';
const pendingForms = new WeakMap();

/**
 * @param {string | number | undefined | null} value
 * @returns {number | null}
 */
function normalizeVariantId(value) {
  const match = String(value ?? '').match(/(\d+)$/);
  return match ? Number(match[1]) : null;
}

/**
 * @param {HTMLFormElement} form
 * @param {string} name
 * @returns {string}
 */
function getFormValue(form, name) {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? field.value : '';
}

/**
 * @param {HTMLInputElement} toggle
 * @returns {{ bundleId: string, index: number } | null}
 */
function getToggleKey(toggle) {
  const dataKey = toggle.dataset.rapiCsToggle;
  const classKey = toggle.className.match(/add_cross_sell_(\d+)_(\d+)/);
  const match = dataKey?.match(/^(\d+)-(\d+)$/) || classKey;

  if (!match) return null;

  return {
    bundleId: match[1],
    index: Number(match[2]),
  };
}

/**
 * @param {Record<string, any>} product
 * @param {HTMLElement | null} card
 * @param {string} bundleId
 * @param {number} index
 * @returns {Record<string, any> | null}
 */
function getSelectedVariant(product, card, bundleId, index) {
  const variants = Array.isArray(product.default_variants) ? product.default_variants : [];
  const listVariant = card?.querySelector(`.rapi_cross_sell_list_variant_${bundleId}_${index}`);
  const listVariantId = normalizeVariantId(
    listVariant instanceof HTMLSelectElement ? listVariant.value : null
  );

  if (listVariantId) {
    const selectedListVariant = variants.find(
      (variant) => normalizeVariantId(variant.shopify_variant_id) === listVariantId
    );
    if (selectedListVariant) return selectedListVariant;
  }

  const selectedOptions = [0, 1, 2].map((optionIndex) => {
    const selector = `.rapi_cross_sell_option_${bundleId}_${index}_${optionIndex}`;
    const field = card?.querySelector(selector) || document.querySelector(selector);
    return field instanceof HTMLSelectElement ? field.value : '';
  });

  const hasSelectedOptions = selectedOptions.some(Boolean);
  if (hasSelectedOptions) {
    const selectedVariant = variants.find(
      (variant) =>
        (!selectedOptions[0] || String(variant.option1) === selectedOptions[0]) &&
        (!selectedOptions[1] || String(variant.option2) === selectedOptions[1]) &&
        (!selectedOptions[2] || String(variant.option3) === selectedOptions[2])
    );
    if (selectedVariant) return selectedVariant;
  }

  return product.default_product || variants[0] || null;
}

/**
 * @param {HTMLFormElement} form
 * @param {string} bundleId
 * @returns {Record<string, string>}
 */
function getRapiProperties(form, bundleId) {
  const rawOffer = getFormValue(form, 'properties[__rapi_offer]');
  let offer = {};

  try {
    offer = rawOffer ? JSON.parse(rawOffer) : {};
  } catch {
    offer = {};
  }

  offer.cross_sell = true;
  delete offer.main;
  delete offer.combo;
  delete offer.is_bb;
  delete offer.upsell;
  delete offer.gift;
  offer.id ||= bundleId;
  offer.offer ||= 'default';

  const properties = {
    _rapi_bundles: getFormValue(form, 'properties[_rapi_bundles]') || bundleId,
    __rapi_offer: JSON.stringify(offer),
  };
  const market = getFormValue(form, 'properties[_rapi_market]');
  const sessionId = getFormValue(form, 'properties[_rapi_session_id]');

  if (market) properties._rapi_market = market;
  if (sessionId) properties._rapi_session_id = sessionId;

  return properties;
}

/**
 * @param {HTMLFormElement} form
 * @returns {Array<{ id: number, quantity: number, properties: Record<string, string> }>}
 */
function getSelectedItems(form) {
  const selected = new Map();

  for (const toggle of form.querySelectorAll(RAPI_TOGGLE_SELECTOR)) {
    if (!(toggle instanceof HTMLInputElement) || !toggle.checked) continue;

    const key = getToggleKey(toggle);
    if (!key) continue;

    const productsByBundle = window.__rapiCrossSellProductsByBundle;
    const products = productsByBundle?.[key.bundleId];
    const product = Array.isArray(products) ? products[key.index] : null;
    if (!product) continue;

    const card = toggle.closest('.rapi-cross-sell-grid-card, .rapi-cross-sell-list-card');
    const variant = getSelectedVariant(product, card, key.bundleId, key.index);
    const variantId = normalizeVariantId(variant?.shopify_variant_id);
    if (!variantId) continue;

    const existing = selected.get(variantId);
    if (existing) {
      existing.quantity += 1;
      continue;
    }

    selected.set(variantId, {
      id: variantId,
      quantity: 1,
      properties: getRapiProperties(form, key.bundleId),
    });
  }

  return [...selected.values()];
}

/**
 * @returns {Promise<{ items?: Array<{ variant_id: number, quantity: number }> }>}
 */
async function getCart() {
  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}cart.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  });

  if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
  return response.json();
}

/**
 * @param {{ items?: Array<{ variant_id: number, quantity: number }> }} cart
 * @returns {Map<number, number>}
 */
function getCartQuantities(cart) {
  const quantities = new Map();

  for (const item of cart.items || []) {
    quantities.set(Number(item.variant_id), Number(item.quantity) || 0);
  }

  return quantities;
}

/**
 * @param {Array<{ id: number, quantity: number, properties: Record<string, string> }>} selectedItems
 * @param {{ items?: Array<{ variant_id: number, quantity: number }> }} beforeCart
 * @param {{ items?: Array<{ variant_id: number, quantity: number }> }} afterCart
 * @returns {Array<{ id: number, quantity: number, properties: Record<string, string> }>}
 */
function getMissingItems(selectedItems, beforeCart, afterCart) {
  const before = getCartQuantities(beforeCart);
  const after = getCartQuantities(afterCart);

  return selectedItems.flatMap((item) => {
    const addedQuantity = (after.get(item.id) || 0) - (before.get(item.id) || 0);
    const missingQuantity = Math.max(0, item.quantity - addedQuantity);

    return missingQuantity > 0 ? [{ ...item, quantity: missingQuantity }] : [];
  });
}

/**
 * @param {Array<{ id: number, quantity: number, properties: Record<string, string> }>} items
 */
async function addMissingItems(items) {
  if (!items.length) return;

  const root = window.Shopify?.routes?.root || '/';
  const previousInternalState = window.__rapiInternalCartAdd;
  window.__rapiInternalCartAdd = true;

  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ items }),
    });

    if (!response.ok) throw new Error(`Cross-sell add failed: ${response.status}`);
  } finally {
    window.__rapiInternalCartAdd = previousInternalState;
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {Event & { action?: string, context?: string, lines?: Array<{ merchandiseId: string }>, promise?: Promise<any> }} cartEvent
 */
async function completeCrossSell(form, cartEvent) {
  const pending = pendingForms.get(form);
  if (!pending || pending.completing) return;

  pending.completing = true;
  document.removeEventListener(CART_LINES_UPDATE_EVENT, pending.handleCartUpdate, true);
  clearTimeout(pending.fallbackTimeout);
  clearTimeout(pending.safetyTimeout);

  try {
    await cartEvent.promise;
    await new Promise((resolve) => setTimeout(resolve, 350));

    const [beforeCart, afterCart] = await Promise.all([pending.beforeCart, getCart()]);
    const missingItems = getMissingItems(pending.items, beforeCart, afterCart);
    await addMissingItems(missingItems);

    if (missingItems.length) {
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { source: 'caleo-rapi-cross-sell' },
        })
      );
      document.dispatchEvent(
        new CustomEvent('theme:cart:updated', {
          detail: { source: 'caleo-rapi-cross-sell' },
        })
      );
    }
  } catch (error) {
    console.error('[CALEO] Rapi cross-sell bridge failed', error);
  } finally {
    pending.submitter?.removeAttribute('aria-busy');
    pendingForms.delete(form);
  }
}

/**
 * @param {SubmitEvent} event
 */
function handleSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.action.includes('/cart/add')) return;

  const items = getSelectedItems(form);
  if (!items.length) return;

  if (pendingForms.has(form)) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  const submitter =
    event.submitter instanceof HTMLButtonElement ? event.submitter : form.querySelector('[type="submit"]');
  const mainVariantId = normalizeVariantId(getFormValue(form, 'id'));
  const beforeCart = getCart().catch(() => ({ items: [] }));

  const handleCartUpdate = (cartEvent) => {
    if (cartEvent.action !== 'add' || cartEvent.context !== 'product') return;
    if (
      mainVariantId &&
      Array.isArray(cartEvent.lines) &&
      !cartEvent.lines.some(
        (line) => normalizeVariantId(line.merchandiseId) === mainVariantId
      )
    ) {
      return;
    }

    document.removeEventListener(CART_LINES_UPDATE_EVENT, handleCartUpdate, true);
    void completeCrossSell(form, cartEvent);
  };

  document.addEventListener(CART_LINES_UPDATE_EVENT, handleCartUpdate, true);

  const fallbackTimeout = window.setTimeout(() => {
    void completeCrossSell(form, {
      promise: Promise.resolve(),
    });
  }, 1200);

  const safetyTimeout = window.setTimeout(() => {
    document.removeEventListener(CART_LINES_UPDATE_EVENT, handleCartUpdate, true);
    submitter?.removeAttribute('aria-busy');
    pendingForms.delete(form);
  }, 15000);

  pendingForms.set(form, {
    items,
    beforeCart,
    submitter,
    handleCartUpdate,
    fallbackTimeout,
    safetyTimeout,
    completing: false,
  });

  submitter?.setAttribute('aria-busy', 'true');
}

document.addEventListener('submit', handleSubmit, true);
